import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Field } from "@/components/UI";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Trash, PencilSimple, Plus, X, Check, CurrencyCircleDollar, Prohibit, ArrowClockwise, DeviceMobile, DesktopTower, SignOut, Eye, ChartBar, Lock } from "@phosphor-icons/react";

const ROLE_LABELS = {
  owner: "Admin (Sahip)",
  manager: "Müdür",
  vet: "Veteriner",
  employee: "Personel",
};

const ROLE_TONES = {
  owner: { bg: "#2D4A36", fg: "#ffffff" },
  manager: { bg: "#8B6914", fg: "#ffffff" },
  vet: { bg: "#DFEAE2", fg: "#417251" },
  employee: { bg: "#EEECE5", fg: "#5A6E5F" },
};

const DEFAULT_FORM = {
  email: "",
  password: "",
  name: "",
  role: "employee",
  allowed_pages: ["panel"],
  can_see_costs: false,
};

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null); // null = create, {id, ...} = edit
  const [form, setForm] = useState(DEFAULT_FORM);
  const [busy, setBusy] = useState(false);
  const [devicesFor, setDevicesFor] = useState(null); // uid — devices modal görünsün
  const [devices, setDevices] = useState([]);
  const [devLoading, setDevLoading] = useState(false);
  const [permMatrix, setPermMatrix] = useState(null); // {visibility_keys, kpi_keys, roles, locked_roles, matrix}

  const load = async () => {
    try {
      const [uRes, pRes, mRes] = await Promise.all([
        api.get("/auth/users"),
        api.get("/auth/pages"),
        api.get("/permissions/matrix"),
      ]);
      setUsers(uRes.data);
      setPages(pRes.data);
      setPermMatrix(mRes.data);
    } catch (e) {
      toast.error(e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const togglePermission = async (section, key, role) => {
    if (!permMatrix) return;
    if (permMatrix.locked_roles.includes(role)) return; // kilitli — değişmez
    const current = permMatrix.matrix[section][key][role];
    const patch = { [section]: { [key]: { [role]: !current } } };
    // Optimistic update
    setPermMatrix(prev => ({
      ...prev,
      matrix: {
        ...prev.matrix,
        [section]: {
          ...prev.matrix[section],
          [key]: { ...prev.matrix[section][key], [role]: !current },
        },
      },
    }));
    try {
      await api.patch("/permissions/matrix", patch);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
      // Rollback
      setPermMatrix(prev => ({
        ...prev,
        matrix: {
          ...prev.matrix,
          [section]: {
            ...prev.matrix[section],
            [key]: { ...prev.matrix[section][key], [role]: current },
          },
        },
      }));
    }
  };

  const startCreate = () => {
    setEditing(null);
    setForm(DEFAULT_FORM);
  };
  const startEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      password: "",
      name: u.name || "",
      role: u.role,
      allowed_pages: [...(u.allowed_pages || [])],
      can_see_costs: !!u.can_see_costs,
    });
  };
  const cancelEdit = () => {
    setEditing(null);
    setForm(DEFAULT_FORM);
  };

  const togglePage = (key) => {
    setForm(f => ({
      ...f,
      allowed_pages: f.allowed_pages.includes(key)
        ? f.allowed_pages.filter(p => p !== key)
        : [...f.allowed_pages, key],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Ad-Soyad zorunlu");
    if (!editing && !form.password) return toast.error("Yeni kullanıcı için şifre girin");
    if (!editing && form.password.length < 6) return toast.error("Şifre en az 6 karakter olmalı");
    setBusy(true);
    try {
      if (editing) {
        const patch = {
          name: form.name,
          role: form.role,
          allowed_pages: form.allowed_pages,
          can_see_costs: form.can_see_costs,
        };
        if (form.password) patch.password = form.password;
        await api.patch(`/auth/users/${editing.id}`, patch);
        toast.success("Kullanıcı güncellendi");
      } else {
        await api.post("/auth/users", form);
        toast.success("Kullanıcı eklendi");
      }
      cancelEdit();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (u.id === me?.id) return toast.error("Kendinizi silemezsiniz");
    if (!window.confirm(`"${u.name || u.email}" kullanıcısı silinsin mi?`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success("Kullanıcı silindi");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const toggleBlock = async (u) => {
    if (u.id === me?.id) return toast.error("Kendinizi bloke edemezsiniz");
    const willBlock = u.is_active !== false;
    const msg = willBlock
      ? `"${u.name || u.email}" kullanıcısı bloke edilsin mi? Kullanıcı sisteme giremeyecek.`
      : `"${u.name || u.email}" kullanıcısının bloğu kaldırılsın mı? Kullanıcı tekrar giriş yapabilir.`;
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/auth/users/${u.id}`, { is_active: !willBlock });
      toast.success(willBlock ? "Kullanıcı bloke edildi" : "Blok kaldırıldı");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const openDevices = async (u) => {
    setDevicesFor(u);
    setDevLoading(true);
    try {
      const r = await api.get(`/auth/users/${u.id}/devices`);
      setDevices(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
      setDevices([]);
    } finally {
      setDevLoading(false);
    }
  };
  const closeDevices = () => { setDevicesFor(null); setDevices([]); };

  const approveDevice = async (d) => {
    try {
      await api.post(`/auth/devices/${d.id}/approve`);
      toast.success("Cihaz onaylandı — kullanıcı artık giriş yapabilir");
      openDevices(devicesFor);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };
  const revokeDevice = async (d) => {
    if (!window.confirm(`"${d.name}" cihazı silinsin mi? Kullanıcı bu cihazdan tekrar giriş yaparsa yeniden onay istenir.`)) return;
    try {
      await api.delete(`/auth/devices/${d.id}`);
      toast.success("Cihaz silindi");
      openDevices(devicesFor);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const logoutAllForUser = async (u) => {
    const target = u || devicesFor;
    if (!target) return;
    if (target.id === me?.id) return toast.error("Kendi oturumunuzu buradan sonlandıramazsınız");
    if (!window.confirm(`"${target.name || target.email}" kullanıcısının TÜM aktif oturumları sonlandırılsın mı? Kullanıcı tüm cihazlarından çıkış yapmış olacak ve sonraki her girişte e-posta doğrulama gerekecek.`)) return;
    try {
      const r = await api.post(`/auth/users/${target.id}/logout-all`);
      toast.success(`${r.data.devices_removed} cihaz silindi — kullanıcı çıkış yaptı`);
      if (devicesFor) openDevices(target);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const isPrivilegedForm = form.role === "owner" || form.role === "manager";
  const roleBadge = (role) => {
    const t = ROLE_TONES[role] || ROLE_TONES.employee;
    return <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: t.bg, color: t.fg }}>{ROLE_LABELS[role] || role}</span>;
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Kullanıcı Yönetimi" subtitle="Hangi kullanıcının hangi sayfaları ve maliyetleri göreceğini yönetin." testId="users-header"
        actions={<Button data-testid="user-new-btn" onClick={startCreate}><Plus size={16} weight="bold" className="inline mr-1" />Yeni Kullanıcı</Button>} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <Card testId="user-form-card">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={18} weight="duotone" className="text-[#2D4A36]" />
            <h3 className="heading text-lg">{editing ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</h3>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <Field label="E-posta *">
              <Input data-testid="user-email" type="email" required disabled={!!editing}
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="dr@ciftlik.com" />
            </Field>
            <Field label="Ad-Soyad *">
              <Input data-testid="user-name-input" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label={editing ? "Yeni Şifre (opsiyonel)" : "Şifre * (en az 6 karakter)"}>
              <Input data-testid="user-password" type="password" placeholder={editing ? "Boş bırakırsanız değişmez" : ""}
                value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
            </Field>
            <Field label="Rol *">
              <Select data-testid="user-role" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                <option value="employee">Personel (Yem Op. / Tekniker)</option>
                <option value="vet">Veteriner</option>
                <option value="manager">Müdür (Her sayfa, kullanıcı yönetimi hariç)</option>
                <option value="owner">Admin / Sahip (Tüm yetkiler + kullanıcı yönetimi)</option>
              </Select>
            </Field>

            {!isPrivilegedForm && (
              <>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#6b746c] mb-2 font-semibold">Sayfa Yetkileri</div>
                  <div className="grid grid-cols-2 gap-1.5" data-testid="user-pages">
                    {pages.map(p => (
                      <label key={p.key} className={`flex items-center gap-2 text-sm px-2.5 py-1.5 rounded border cursor-pointer transition-colors ${
                        form.allowed_pages.includes(p.key) ? "bg-[#E4EEE6] border-[#417251] text-[#2D4A36]" : "bg-white border-[#E5E2DC] text-[#5e675e] hover:bg-[#F7F6F3]"
                      }`}>
                        <input type="checkbox" className="accent-[#2D4A36]"
                          checked={form.allowed_pages.includes(p.key)}
                          onChange={() => togglePage(p.key)}
                          data-testid={`user-page-${p.key}`} />
                        <span className="text-xs">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <label className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition-colors ${
                  form.can_see_costs ? "bg-[#F7EBCC] border-[#DDA74F]" : "bg-white border-[#E5E2DC]"
                }`}>
                  <input type="checkbox" className="accent-[#8B6914] mt-0.5"
                    checked={form.can_see_costs}
                    onChange={e => setForm({ ...form, can_see_costs: e.target.checked })}
                    data-testid="user-see-costs" />
                  <div>
                    <div className="text-sm font-semibold flex items-center gap-1"><CurrencyCircleDollar size={14} weight="fill" />Maliyet Görebilir</div>
                    <div className="text-xs text-[#5e675e]">Tedavi maliyeti, rasyon maliyeti, ilaç fiyatları görünür olsun.</div>
                  </div>
                </label>
              </>
            )}

            {isPrivilegedForm && (
              <div className="p-3 bg-[#E4EEE6] rounded-md text-xs text-[#2D4A36]">
                {form.role === "owner"
                  ? "🔒 Admin rolü: Tüm sayfalar + Kullanıcı Yönetimi + maliyet erişimi otomatik verilir."
                  : "👔 Müdür rolü: Tüm sayfalar + maliyet erişimi otomatik verilir. Kullanıcı Yönetimi görünmez."}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button data-testid="user-submit" type="submit" disabled={busy}>
                {editing ? <><Check size={16} weight="bold" className="inline mr-1" />Güncelle</> : <><Plus size={16} weight="bold" className="inline mr-1" />Ekle</>}
              </Button>
              {editing && (
                <Button data-testid="user-cancel" type="button" variant="secondary" onClick={cancelEdit}>
                  <X size={16} weight="bold" className="inline mr-1" />İptal
                </Button>
              )}
            </div>
          </form>
        </Card>

        {/* User list */}
        <div className="lg:col-span-2">
          <Card testId="user-list-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="heading text-lg">Kullanıcılar ({users.length})</h3>
            </div>
            <table className="w-full text-sm" data-testid="user-table">
              <thead>
                <tr className="text-left border-b border-[#EEECE5] text-xs uppercase tracking-widest text-[#6b746c]">
                  <th className="pb-2">Kullanıcı</th>
                  <th>Rol</th>
                  <th>Sayfalar</th>
                  <th>Maliyet</th>
                  <th>Durum</th>
                  <th className="text-right">Aksiyonlar</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const active = u.is_active !== false;
                  return (
                    <tr key={u.id} className={`border-b border-[#F5F2EC] ${active ? "" : "opacity-60 bg-[#fbeee9]"}`} data-testid={`user-row-${u.id}`}>
                      <td className="py-3">
                        <div className="font-semibold flex items-center gap-1.5">
                          {u.name || "—"}
                          {!active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#C45B45] text-white font-bold">BLOKE</span>}
                        </div>
                        <div className="text-xs text-[#8a938c]">{u.email}</div>
                      </td>
                      <td>{roleBadge(u.role)}</td>
                      <td className="text-xs">
                        {u.role === "owner" || u.role === "manager" ? (
                          <span className="text-[#2D4A36] italic">Tüm sayfalar</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {(u.allowed_pages || []).slice(0, 4).map(p => (
                              <span key={p} className="px-1.5 py-0.5 rounded bg-[#F5F2EC] text-[#5e675e]">
                                {pages.find(x => x.key === p)?.label || p}
                              </span>
                            ))}
                            {(u.allowed_pages || []).length > 4 && (
                              <span className="text-[#8a938c] self-center">+{u.allowed_pages.length - 4}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {u.can_see_costs ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#F7EBCC] text-[#7a5a10]">✓ Görebilir</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#EEECE5] text-[#8a938c]">✗ Gizli</span>
                        )}
                      </td>
                      <td>
                        {active ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#E4EEE6] text-[#2D4A36]">● Aktif</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#FBE9E4] text-[#8a3a2b]">● Bloke</span>
                        )}
                      </td>
                      <td className="text-right">
                        <div className="inline-flex gap-1">
                          <button data-testid={`user-devices-${u.id}`} onClick={() => openDevices(u)}
                            className="p-1.5 rounded hover:bg-[#F5F2EC] text-[#417251]" title="Cihazlar">
                            <DeviceMobile size={16} weight="duotone" />
                          </button>
                          <button data-testid={`user-edit-${u.id}`} onClick={() => startEdit(u)}
                            className="p-1.5 rounded hover:bg-[#F5F2EC] text-[#417251]" title="Düzenle">
                            <PencilSimple size={16} weight="duotone" />
                          </button>
                          {u.id !== me?.id && (
                            <>
                              <button data-testid={`user-block-${u.id}`} onClick={() => toggleBlock(u)}
                                className={`p-1.5 rounded ${active ? "hover:bg-[#fbeee9] text-[#C45B45]" : "hover:bg-[#E4EEE6] text-[#2D4A36]"}`}
                                title={active ? "Bloke et" : "Blok'u kaldır"}>
                                {active ? <Prohibit size={16} weight="duotone" /> : <ArrowClockwise size={16} weight="duotone" />}
                              </button>
                              <button data-testid={`user-delete-${u.id}`} onClick={() => remove(u)}
                                className="p-1.5 rounded hover:bg-[#fbeee9] text-[#C45B45]" title="Sil">
                                <Trash size={16} weight="duotone" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!users.length && (
                  <tr><td colSpan={6} className="py-8 text-center text-[#8a938c]">Henüz kullanıcı yok</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </div>
      </div>

      {/* Yetki Matrisi */}
      {permMatrix && <PermissionMatrixSection matrix={permMatrix} toggle={togglePermission} />}

      {/* Devices Modal */}
      {devicesFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={closeDevices} data-testid="devices-modal">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[#E5E2DC] flex items-center justify-between">
              <div>
                <h3 className="heading text-xl flex items-center gap-2">
                  <DeviceMobile size={22} weight="duotone" className="text-[#417251]" />
                  Cihazlar — {devicesFor.name || devicesFor.email}
                </h3>
                <div className="text-xs text-[#8a938c] mt-0.5">{devicesFor.email}</div>
              </div>
              <div className="flex items-center gap-2">
                {devicesFor.id !== me?.id && devices.length > 0 && (
                  <button onClick={() => logoutAllForUser()} data-testid="devices-logout-all"
                    className="text-xs px-3 py-1.5 rounded bg-[#C45B45] text-white hover:bg-[#a04732] font-semibold flex items-center gap-1">
                    <SignOut size={14} weight="bold" /> Tüm Oturumları Sonlandır
                  </button>
                )}
                <button onClick={closeDevices} data-testid="devices-close" className="p-2 rounded hover:bg-[#F5F2EC]"><X size={20} /></button>
              </div>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {devLoading ? (
                <div className="text-center py-8 text-[#8a938c]">Yükleniyor...</div>
              ) : !devices.length ? (
                <div className="text-center py-8 text-[#8a938c]">Bu kullanıcının henüz kayıtlı cihazı yok.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-[#EEECE5] text-xs uppercase tracking-widest text-[#6b746c]">
                      <th className="pb-2">Cihaz</th>
                      <th>Son Giriş</th>
                      <th>E-posta</th>
                      <th>Admin</th>
                      <th className="text-right">Aksiyonlar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map(d => {
                      const ua = (d.user_agent || "").toLowerCase();
                      const isMobile = ua.includes("iphone") || ua.includes("android") || ua.includes("ipad");
                      return (
                        <tr key={d.id} className="border-b border-[#F5F2EC]" data-testid={`device-row-${d.id}`}>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              {isMobile ? <DeviceMobile size={18} className="text-[#417251]" /> : <DesktopTower size={18} className="text-[#417251]" />}
                              <div>
                                <div className="font-semibold">{d.name}</div>
                                <div className="text-[10px] text-[#8a938c] truncate max-w-[240px]">{d.user_agent || "—"}</div>
                              </div>
                            </div>
                          </td>
                          <td className="text-xs tabnum">{d.last_seen ? new Date(d.last_seen).toLocaleString("tr-TR") : "—"}</td>
                          <td>{d.email_verified ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#E4EEE6] text-[#2D4A36]">✓ Doğrulandı</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-[#FBE9E4] text-[#8a3a2b]">✗ Bekliyor</span>}</td>
                          <td>{d.admin_approved ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#E4EEE6] text-[#2D4A36]">✓ Onaylı</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-[#F7EBCC] text-[#7a5a10]">⏳ Onay Bekliyor</span>}</td>
                          <td className="text-right">
                            <div className="inline-flex gap-1">
                              {!d.admin_approved && (
                                <button data-testid={`device-approve-${d.id}`} onClick={() => approveDevice(d)}
                                  className="text-xs px-2 py-1 rounded bg-[#2D4A36] text-white hover:bg-[#3d5f45] font-semibold">
                                  ✓ Onayla
                                </button>
                              )}
                              <button data-testid={`device-revoke-${d.id}`} onClick={() => revokeDevice(d)}
                                className="p-1.5 rounded hover:bg-[#fbeee9] text-[#C45B45]" title="Cihazı sil">
                                <Trash size={16} weight="duotone" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-3 border-t border-[#EEECE5] bg-[#FAF8F4] text-xs text-[#5e675e]">
              💡 <b>Bilgi:</b> Bir cihazdan giriş yapıldığında burada kayıt oluşur. Onay bekleyen cihazlara "Onayla" tıklayınca kullanıcı o cihazdan giriş yapabilir. "Sil" ile cihaz güveninden çıkarılır — kullanıcı yeniden giriş isterse tekrar onay istenir.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------
// PERMISSION MATRIX SECTION — Görünürlük + KPI checkbox tablosu
// -----------------------------------------------------------
function PermissionMatrixSection({ matrix, toggle }) {
  const { visibility_keys, kpi_keys, roles, locked_roles, matrix: cur } = matrix;
  const isLocked = (role) => locked_roles.includes(role);

  const MatrixTable = ({ title, subtitle, icon: Icon, section, keys }) => (
    <Card testId={`matrix-${section}`} className="!p-0 overflow-hidden">
      <div className="p-4 border-b border-[#EEECE5] flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#E4EEE6]">
          <Icon size={22} weight="duotone" className="text-[#2D4A36]" />
        </div>
        <div>
          <h3 className="heading text-lg">{title}</h3>
          <div className="text-xs text-[#8a938c]">{subtitle}</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#EEECE5] bg-[#FAF8F4]">
              <th className="text-left p-3 text-xs uppercase tracking-widest text-[#6b746c]">Veri / Öğe</th>
              {roles.map(r => (
                <th key={r} className="p-3 text-center min-w-[110px]">
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-xs font-semibold" style={{ color: ROLE_TONES[r]?.bg, filter: "brightness(0.7)" }}>
                      {ROLE_LABELS[r]}
                    </span>
                    {isLocked(r) && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] text-[#8a938c]">
                        <Lock size={9} weight="fill" /> Kilitli
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {keys.map(({ key, label }) => (
              <tr key={key} className="border-b border-[#F5F2EC] hover:bg-[#FAF8F4]" data-testid={`matrix-row-${key}`}>
                <td className="p-3 font-medium text-[#1A1D1A]">{label}</td>
                {roles.map(r => {
                  const checked = !!cur[section]?.[key]?.[r];
                  const locked = isLocked(r);
                  return (
                    <td key={r} className="p-3 text-center">
                      <button
                        type="button"
                        disabled={locked}
                        onClick={() => toggle(section, key, r)}
                        data-testid={`matrix-cell-${section}-${key}-${r}`}
                        className={`inline-flex items-center justify-center w-7 h-7 rounded transition-all ${
                          locked
                            ? "bg-[#E4EEE6] text-[#2D4A36] cursor-not-allowed opacity-70"
                            : checked
                              ? "bg-[#2D4A36] text-white hover:bg-[#3d5f45] shadow-sm"
                              : "bg-white border-2 border-[#E5E2DC] text-transparent hover:border-[#417251] hover:bg-[#F5F2EC]"
                        }`}
                      >
                        {(checked || locked) && <Check size={16} weight="bold" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );

  return (
    <div className="mt-8 space-y-6" data-testid="permission-matrix">
      <div className="border-t border-[#E5E2DC] pt-6">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={24} weight="duotone" className="text-[#2D4A36]" />
          <h2 className="heading text-2xl">Yetki Matrisi</h2>
        </div>
        <p className="text-sm text-[#5e675e] mb-6 max-w-3xl">
          Hangi rolün hangi veriyi ve dashboard KPI'sını göreceğini burada belirleyin. Kutuya tıklayıp ✓ işaretleyin — otomatik kaydolur. <b>Admin ve Müdür</b> kilitlidir (her zaman tümünü görür). Değişiklikler ilgili kullanıcı yeniden giriş yaptığında etkin olur.
        </p>
      </div>

      <MatrixTable
        title="Görünürlük (Veri Mahremiyeti)"
        subtitle="Fiyat, maliyet ve satış bilgileri kimin gözüne görünsün?"
        icon={Eye}
        section="visibility"
        keys={visibility_keys}
      />

      <MatrixTable
        title="Dashboard KPI'ları"
        subtitle="Panelde hangi KPI kartı hangi role gözüksün?"
        icon={ChartBar}
        section="kpis"
        keys={kpi_keys}
      />

      <div className="text-xs text-[#5e675e] p-4 bg-[#F5F2EC] rounded-md">
        💡 <b>İpucu:</b> Yaptığınız değişiklikler <b>rol bazlıdır</b> — o roldeki tüm kullanıcıları etkiler. Kilitli sütunlar (Admin/Müdür) her zaman tümünü görür. Örneğin "Süt birim fiyat" satırında Veteriner için işareti kaldırırsanız, tüm veterinerler süt fiyatlarını göremez.
      </div>
    </div>
  );
}
